import { createAsyncThunk } from '@reduxjs/toolkit';
import { couriersApi } from './couriersApi';

export const fetchCouriers = createAsyncThunk(
  'couriers/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().couriers;
      const params = {
        page: filters.page,
        page_size: filters.rowsPerPage,
        search: filters.search || undefined,
      };

      const data = await couriersApi.getAdminSettingCouriers(params);

      const results = Array.isArray(data) ? data : data.results;
      const count = Array.isArray(data) ? data.length : data.count;

      return {
        results: results || [],
        count: count || 0
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al obtener los proveedores externos'
      );
    }
  }
);

/**
 * The same table, read as the internal team: every operator's couriers.
 *
 * It writes into the same slice as `fetchCouriers` — a session is either staff
 * or it is not, so the two never compete for the list.
 */
export const fetchStaffCouriers = createAsyncThunk(
  'couriers/fetchStaffAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().couriers;
      const data = await couriersApi.getStaffSettingCouriers({
        page: filters.page,
        page_size: filters.rowsPerPage,
      });

      return { results: data.results || [], count: data.count || 0 };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al obtener los proveedores de la plataforma'
      );
    }
  }
);

export const fetchTemplates = createAsyncThunk(
  'couriers/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const data = await couriersApi.getCourierTemplates();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al obtener los templates'
      );
    }
  }
);

export const createCourierThunk = createAsyncThunk(
  'couriers/create',
  async ({ courierData, adminId, isStaff } = {}, { dispatch, rejectWithValue }) => {
    try {
      await couriersApi.createCourier(courierData, adminId);
      // Awaited, so the action is still running until the list shows its result.
      await dispatch(isStaff ? fetchStaffCouriers() : fetchCouriers());
      return true;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear el proveedor externo'
      );
    }
  }
);

export const updateCourierThunk = createAsyncThunk(
  'couriers/update',
  async ({ courierId, courierData, isStaff }, { dispatch, rejectWithValue }) => {
    try {
      await couriersApi.updateCourier(courierId, courierData);
      await dispatch(isStaff ? fetchStaffCouriers() : fetchCouriers());
      return true;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar el proveedor externo'
      );
    }
  }
);

export const deleteCourierThunk = createAsyncThunk(
  'couriers/delete',
  async ({ courierId, isStaff } = {}, { dispatch, rejectWithValue }) => {
    try {
      await couriersApi.deleteCourier(courierId);
      // Until the list comes back without it, the row is still there to click.
      await dispatch(isStaff ? fetchStaffCouriers() : fetchCouriers());
      return courierId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar el proveedor externo'
      );
    }
  },
  {
    // The row being deleted, so only its button waits.
    getPendingMeta: ({ arg }) => ({ pendingKey: arg?.courierId }),
  }
);
